import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Plus, AlertCircle } from 'lucide-react';
import { RetiradorForm } from './RetiradorForm';
import type { RetiradorData, RetiradorErrors } from '../../types';

interface RetirarCopiasStep2MultipleProps {
  cantidadCopias: number;
  fechaRetiro: string;
  onFechaChange: (fecha: string) => void;
  retiradores: RetiradorData[];
  onRetiradoresChange: (retiradores: RetiradorData[]) => void;
  errorsArray?: RetiradorErrors[];
  disabled?: boolean;
}

const createEmptyRetirador = (): RetiradorData => ({
  nombreCompleto: '',
  cantidadCopias: 1,
  fotoDocumentoFrente: null,
  fotoDocumentoDorso: null,
  firmaDigital: null,
});

export const RetirarCopiasStep2Multiple: React.FC<RetirarCopiasStep2MultipleProps> = ({
  cantidadCopias,
  fechaRetiro,
  onFechaChange,
  retiradores,
  onRetiradoresChange,
  errorsArray = [],
  disabled = false,
}) => {
  const totalAsignado = useMemo(() => {
    return retiradores.reduce((sum, r) => sum + (r.cantidadCopias || 0), 0);
  }, [retiradores]);

  const copiasRestantes = cantidadCopias - totalAsignado;
  const isComplete = totalAsignado === cantidadCopias;

  const handleAddRetirador = () => {
    if (copiasRestantes > 0) {
      onRetiradoresChange([...retiradores, createEmptyRetirador()]);
    }
  };

  const handleRemoveRetirador = (index: number) => {
    if (retiradores.length > 1) {
      const updated = retiradores.filter((_, i) => i !== index);
      onRetiradoresChange(updated);
    }
  };

  const handleRetiradorChange = (index: number, retirador: RetiradorData) => {
    const updated = [...retiradores];
    updated[index] = retirador;
    onRetiradoresChange(updated);
  };

  const getMaxCopiasForRetirador = (currentIndex: number): number => {
    const otherRetiradores = retiradores.filter((_, i) => i !== currentIndex);
    const otherTotal = otherRetiradores.reduce((sum, r) => sum + (r.cantidadCopias || 0), 0);
    return cantidadCopias - otherTotal;
  };

  return (
    <div className="space-y-6 min-w-0 overflow-hidden">
      {/* Info de copias */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
        <p className="text-sm text-blue-900">
          Distribuir <span className="font-semibold">{cantidadCopias}</span> {cantidadCopias === 1 ? 'copia' : 'copias'} entre varias personas
        </p>
      </div>

      {/* Fecha de retiro */}
      <div className="space-y-2">
        <Label htmlFor="fecha-retiro-multiple">
          Fecha de Retiro
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="fecha-retiro-multiple"
          type="date"
          value={fechaRetiro}
          onChange={(e) => onFechaChange(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          disabled={disabled}
        />
      </div>

      {/* Estado de distribución */}
      <div className={`rounded-lg border p-4 ${isComplete ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
        <div className="flex items-center gap-2">
          {!isComplete && <AlertCircle className="h-5 w-5 text-yellow-600" />}
          <div className="flex-1">
            <p className="text-sm font-medium">
              Total asignado: <span className={isComplete ? 'text-green-700' : 'text-yellow-700'}>{totalAsignado}/{cantidadCopias}</span>
            </p>
            {!isComplete && (
              <p className="text-xs text-yellow-600 mt-1">
                Falta asignar {copiasRestantes} {copiasRestantes === 1 ? 'copia' : 'copias'}
              </p>
            )}
            {isComplete && (
              <p className="text-xs text-green-600 mt-1">
                ✓ Todas las copias han sido asignadas
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Lista de retiradores */}
      <div className="space-y-4">
        {retiradores.map((retirador, index) => (
          <RetiradorForm
            key={index}
            index={index}
            retirador={retirador}
            onChange={(updated) => handleRetiradorChange(index, updated)}
            errors={errorsArray[index]}
            maxCopias={getMaxCopiasForRetirador(index)}
            onRemove={() => handleRemoveRetirador(index)}
            showRemoveButton={retiradores.length > 1}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Botón agregar persona */}
      {copiasRestantes > 0 && (
        <Button
          type="button"
          variant="outline"
          onClick={handleAddRetirador}
          disabled={disabled}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Persona
        </Button>
      )}

      {/* Advertencia si faltan copias */}
      {!isComplete && retiradores.length > 0 && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <p className="text-sm text-yellow-800">
            <span className="font-medium">Atención:</span> Debe asignar todas las {cantidadCopias} copias antes de continuar.
          </p>
        </div>
      )}
    </div>
  );
};
